import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { Modal } from '@shared/ui/modal/modal';
import { Button } from '@shared/ui/button/button';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { useProfile } from '@shared/api/useProfile';
import { useUpdateUserPreference } from '@widgets/onboarding/api/useUpdateUserPreference';
import {
	START_DAY_OPTIONS,
	START_TIME_OPTIONS,
	getLevelOptionsByInstrumentValue,
	getStartWhenValue,
	parseStartWhenValue,
	resolveInstrumentValueByProfileInstrument,
} from '@entities/student/lib/student-onboarding-options';
import s from './matching-preferences-modal.module.scss';

// Модалка редактирования параметров подбора преподавателя:
// инструмент + уровень + время занятий (start_when: days_type + time_of_day).
// Стили — как в slot-offer-modal.
export const MatchingPreferencesModal = ({ isOpen, close }) => {
	const { data: profile } = useProfile();
	const [selectedInstrumentId, setSelectedInstrumentId] = useState(null);
	const [selectedLevel, setSelectedLevel] = useState('');
	const [selectedDays, setSelectedDays] = useState([]);
	const [selectedTimes, setSelectedTimes] = useState([]);

	const { mutate, isLoading } = useUpdateUserPreference({ onSuccess: close });

	// При открытии подставляем текущие предпочтения из профиля
	useEffect(() => {
		if (!isOpen) return;
		const prefs = profile?.student_preferences || {};
		setSelectedInstrumentId(prefs.instrument_id || profile?.instruments?.[0]?.id || null);
		setSelectedLevel(prefs.level || '');
		const { days, times } = parseStartWhenValue(prefs);
		setSelectedDays(days);
		setSelectedTimes(times);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const selectedInstrument = useMemo(
		() => profile?.instruments?.find((instrument) => instrument.id === selectedInstrumentId) || null,
		[profile?.instruments, selectedInstrumentId]
	);
	const instrumentValue = resolveInstrumentValueByProfileInstrument(selectedInstrument);
	const levelOptions = useMemo(
		() => getLevelOptionsByInstrumentValue(instrumentValue),
		[instrumentValue]
	);

	// Смена инструмента сбрасывает уровень, если он недоступен для нового инструмента
	useEffect(() => {
		if (selectedLevel && !levelOptions.some((option) => option.value === selectedLevel)) {
			setSelectedLevel('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [instrumentValue]);

	const toggleInList = (list, setList, value) => {
		setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
	};

	const canSave = Boolean(
		selectedInstrumentId && selectedLevel && selectedDays.length && selectedTimes.length
	);

	const handleSave = () => {
		if (!canSave) return;
		mutate({
			student_preferences: {
				instrument: instrumentValue,
				instrument_id: selectedInstrumentId,
				level: selectedLevel,
				...getStartWhenValue({ days: selectedDays, times: selectedTimes }),
			},
		});
	};

	if (!isOpen) return null;

	const modalStyle = {
		content: {
			width: 'calc(100% - 40px)',
			maxWidth: '420px',
			maxHeight: 'calc(100svh - 40px)',
			padding: '20px',
			overflow: 'auto',
		},
	};

	return (
		<Modal {...{ isOpen, close }} title="Параметры подбора" style={modalStyle}>
			<motion.div
				className={s.card}
				initial={{ opacity: 0, y: 8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.25, ease: 'easeInOut' }}
			>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Инструмент</h3>
					<div className={s.options}>
						{profile?.instruments?.map((instrument) => (
							<button
								key={instrument.id}
								type="button"
								className={classNames(
									s.option,
									selectedInstrumentId === instrument.id && s.active
								)}
								onClick={() => setSelectedInstrumentId(instrument.id)}
							>
								<TextWithEmoji text={instrument.name} />
							</button>
						))}
					</div>
				</div>

				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Дни занятий</h3>
					<div className={s.options}>
						{START_DAY_OPTIONS.map((option) => (
							<button
								key={option.value}
								type="button"
								className={classNames(s.option, selectedDays.includes(option.value) && s.active)}
								onClick={() => toggleInList(selectedDays, setSelectedDays, option.value)}
							>
								{option.label}
							</button>
						))}
					</div>
				</div>

				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Время</h3>
					<div className={s.options}>
						{START_TIME_OPTIONS.map((option) => (
							<button
								key={option.value}
								type="button"
								className={classNames(s.option, selectedTimes.includes(option.value) && s.active)}
								onClick={() => toggleInList(selectedTimes, setSelectedTimes, option.value)}
							>
								{option.label}
							</button>
						))}
					</div>
				</div>

				<div className={s.footer}>
					<Button
						label="Сохранить"
						onClick={handleSave}
						disabled={!canSave}
						fetching={isLoading}
						className={s.submitBtn}
					/>
				</div>
			</motion.div>
		</Modal>
	);
};
